
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const ProjectsPage = () => {
  const featuredProjects = [
    {
      id: 1,
      title: 'Automotive Dashboard Components',
      client: 'Major European Automaker',
      industry: 'Automotive',
      description: 'Precision injection molded dashboard components with integrated sensor housings. Delivered 2.5 million units with 99.97% quality rate.',
      image: 'https://public.readdy.ai/ai/img_res/9e37321e7a8d70cc8d5a83101b132913.jpg',
      stats: { units: '2.5M+', quality: '99.97%', timeline: '18 months' },
      services: ['Injection Molding', 'Toolmaking'],
    },
    {
      id: 2,
      title: 'Medical Device Housings',
      client: 'Leading Medical Equipment Manufacturer',
      industry: 'Medical',
      description: 'ISO 13485 certified production of sterile medical device housings. Clean room manufacturing with full traceability.',
      image: 'https://readdy.ai/api/search-image?query=Medical%20device%20plastic%20housing%20components%20white%20sterile%20clean%20room%20manufactured%20precision%20parts%20professional%20product%20photography%20healthcare%20equipment&width=600&height=400&seq=proj2&orientation=landscape',
      stats: { units: '500K+', quality: '99.99%', timeline: '24 months' },
      services: ['Injection Molding', '3D Printing'],
    },
    {
      id: 3,
      title: 'Consumer Electronics Enclosures',
      client: 'Global Tech Company',
      industry: 'Electronics',
      description: 'High-gloss consumer electronics enclosures with complex geometries. Multi-shot molding with soft-touch overmolding.',
      image: 'https://readdy.ai/api/search-image?query=Consumer%20electronics%20plastic%20enclosure%20sleek%20modern%20design%20high%20gloss%20finish%20professional%20product%20photography%20tech%20gadget%20housing%20white%20background&width=600&height=400&seq=proj3&orientation=landscape',
      stats: { units: '1.2M+', quality: '99.95%', timeline: '12 months' },
      services: ['Injection Molding', 'Blow Molding'],
    },
    {
      id: 4,
      title: 'Aerospace Component Prototypes',
      client: 'European Aerospace Consortium',
      industry: 'Aerospace',
      description: 'Rapid prototyping and low-volume production of aerospace-grade components. AS9100 certified manufacturing process.',
      image: 'https://readdy.ai/api/search-image?query=Aerospace%20precision%20plastic%20components%20lightweight%20engineering%20parts%20professional%20product%20photography%20aviation%20industry%20high%20performance%20materials%20white%20background&width=600&height=400&seq=proj4&orientation=landscape',
      stats: { units: '10K+', quality: '99.99%', timeline: '8 months' },
      services: ['3D Printing', 'Toolmaking'],
    },
    {
      id: 5,
      title: 'Beverage Container Production',
      client: 'International Beverage Brand',
      industry: 'Packaging',
      description: 'High-volume blow molding production of PET beverage containers. Sustainable manufacturing with 30% recycled content.',
      image: 'https://readdy.ai/api/search-image?query=Plastic%20beverage%20bottles%20PET%20containers%20blow%20molded%20transparent%20packaging%20professional%20product%20photography%20clean%20white%20background%20manufacturing&width=600&height=400&seq=proj5&orientation=landscape',
      stats: { units: '50M+', quality: '99.9%', timeline: '36 months' },
      services: ['Blow Molding'],
    },
    {
      id: 6,
      title: 'Industrial Equipment Parts',
      client: 'German Industrial Machinery Company',
      industry: 'Industrial',
      description: 'Heavy-duty industrial components with high wear resistance. Engineering-grade polymers for demanding applications.',
      image: 'https://readdy.ai/api/search-image?query=Industrial%20plastic%20machinery%20components%20heavy%20duty%20engineering%20parts%20professional%20product%20photography%20manufacturing%20equipment%20white%20background&width=600&height=400&seq=proj6&orientation=landscape',
      stats: { units: '200K+', quality: '99.96%', timeline: '24 months' },
      services: ['Injection Molding', 'Toolmaking'],
    },
  ];

  const industries = [
    { name: 'Automotive', icon: 'ri-car-line', projects: 45 },
    { name: 'Medical', icon: 'ri-heart-pulse-line', projects: 32 },
    { name: 'Electronics', icon: 'ri-cpu-line', projects: 58 },
    { name: 'Aerospace', icon: 'ri-plane-line', projects: 18 },
    { name: 'Packaging', icon: 'ri-box-3-line', projects: 67 },
    { name: 'Industrial', icon: 'ri-settings-3-line', projects: 41 },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20precision%20manufacturing%20facility%20interior%20with%20advanced%20machinery%20and%20robotic%20arms%20industrial%20production%20line%20clean%20professional%20environment%20blue%20and%20white%20tones&width=1920&height=800&seq=projhero&orientation=landscape"
            alt="Alpine Precision Mold Manufacturing Projects"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/80 to-slate-900/70"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-1.5 bg-primary-500/20 text-primary-300 text-sm font-medium rounded-full mb-6">
              Our Portfolio
            </span>
            <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-6">
              Past Projects & Success Stories
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-8">
              Explore our track record of delivering precision manufacturing solutions across diverse industries. From automotive to aerospace, we've helped leading companies bring their products to life.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-4">
                <div className="text-3xl font-bold text-white">260+</div>
                <div className="text-sm text-slate-300">Projects Completed</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-4">
                <div className="text-3xl font-bold text-white">99.95%</div>
                <div className="text-sm text-slate-300">Average Quality Rate</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-4">
                <div className="text-3xl font-bold text-white">50M+</div>
                <div className="text-sm text-slate-300">Parts Manufactured</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display font-bold text-3xl text-slate-900 mb-4">Industries We Serve</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our expertise spans across multiple sectors, delivering tailored solutions for each industry's unique requirements.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {industries.map((industry) => (
              <div
                key={industry.name}
                className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow cursor-pointer group"
              >
                <div className="w-14 h-14 bg-primary-50 group-hover:bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4 transition-colors">
                  <i className={`${industry.icon} text-2xl text-primary-600`}></i>
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{industry.name}</h3>
                <p className="text-sm text-slate-500">{industry.projects} projects</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-50 text-primary-600 text-sm font-medium rounded-full mb-4">
              Featured Work
            </span>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-slate-900 mb-4">
              Showcase of Excellence
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Discover how we've helped industry leaders achieve their manufacturing goals with precision and efficiency.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProjects.map((project) => (
              <div
                key={project.id}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-slate-700 text-xs font-medium rounded-full">
                      {project.industry}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-display font-semibold text-xl text-slate-900 mb-2">
                    {project.title}
                  </h3>
                  <p className="text-sm text-primary-600 font-medium mb-3">{project.client}</p>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">{project.description}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.services.map((service) => (
                      <span
                        key={service}
                        className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-md"
                      >
                        {service}
                      </span>
                    ))}
                  </div>

                  <div className="grid grid-cols-3 gap-2 pt-4 border-t border-slate-100">
                    <div className="text-center">
                      <div className="text-lg font-bold text-slate-900">{project.stats.units}</div>
                      <div className="text-xs text-slate-500">Units</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-primary-600">{project.stats.quality}</div>
                      <div className="text-xs text-slate-500">Quality</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-slate-900">{project.stats.timeline}</div>
                      <div className="text-xs text-slate-500">Duration</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-500/20 text-primary-300 text-sm font-medium rounded-full mb-4">
              Client Testimonials
            </span>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-white mb-4">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                ))}
              </div>
              <p className="text-slate-300 mb-6 leading-relaxed">
                "Alpine Precision Molding delivered exceptional quality on our automotive components. Their attention to detail and commitment to deadlines exceeded our expectations."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-primary-500/20 rounded-full flex items-center justify-center mr-4">
                  <span className="text-primary-400 font-bold">MK</span>
                </div>
                <div>
                  <div className="font-semibold text-white">Michael Krause</div>
                  <div className="text-sm text-slate-400">VP Engineering, AutoTech GmbH</div>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                ))}
              </div>
              <p className="text-slate-300 mb-6 leading-relaxed">
                "The team's expertise in medical-grade manufacturing gave us confidence throughout the project. Their ISO certifications and quality processes are truly world-class."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-primary-500/20 rounded-full flex items-center justify-center mr-4">
                  <span className="text-primary-400 font-bold">SL</span>
                </div>
                <div>
                  <div className="font-semibold text-white">Dr. Sarah Liu</div>
                  <div className="text-sm text-slate-400">Director, MedDevice Solutions</div>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                ))}
              </div>
              <p className="text-slate-300 mb-6 leading-relaxed">
                "From prototype to production, Alpine Precision Molding was a true partner. Their competitive pricing without compromising quality made them our go-to manufacturer."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-primary-500/20 rounded-full flex items-center justify-center mr-4">
                  <span className="text-primary-400 font-bold">JV</span>
                </div>
                <div>
                  <div className="font-semibold text-white">Johan van der Berg</div>
                  <div className="text-sm text-slate-400">CEO, TechPack Industries</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-3xl md:text-4xl text-slate-900 mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Join the ranks of industry leaders who trust Alpine Precision Molding for their manufacturing needs.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/instant-quote"
              className="px-8 py-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-base font-semibold rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-500/30 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-flashlight-line mr-2"></i>
              Get Instant Quote
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 bg-white border border-slate-200 text-slate-700 text-base font-semibold rounded-lg hover:bg-slate-50 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line mr-2"></i>
              Contact Our Team
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectsPage;
